import React from "react";
import { Building2 } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage, StatusBadge, getAvatarInitials } from "../ui";
import { formatCpfCnpj, getCpfCnpjLabel } from "../../utils/inputMasks";

export const PARCEIRO_STATUS_LABEL = { ativo: "Ativo", lead: "Lead", inativo: "Inativo" };

// Nome para exibição: muitos cadastros vêm como "49.837.758 NOME DA EMPRESA" (raiz do CNPJ ou CPF na
// frente). O documento já aparece em linha própria, então sai do título e do breadcrumb. Só remove
// um prefixo com 8+ dígitos; nome que é só número fica como está.
export function nomeExibicaoParceiro(nome) {
  const original = String(nome || "").trim();
  const match = original.match(/^([\d.\-/]+)\s*[-–]?\s+(.+)$/);
  if (!match || match[1].replace(/\D/g, "").length < 8) return original;
  return match[2].trim();
}

/**
 * Faixa de identidade do parceiro: nome, status, documento, ID Join, cidade e responsável. Usada no
 * topo dos detalhes do parceiro e da vida do agente detalhada, no lugar de título + botão Voltar (o
 * breadcrumb da barra superior já faz esse papel). Lê o cadastro salvo, nunca um formulário em edição.
 *
 * onNomeClick: quando informado, o nome vira link (ex.: da vida do agente de volta aos detalhes).
 */
export default function ParceiroIdentidade({ parceiro, fallbackNome = "Parceiro", onNomeClick }) {
  const documento = formatCpfCnpj(parceiro?.documento || "");
  const local = [parceiro?.cidade, parceiro?.uf].filter(Boolean).join("/");
  const detalhes = [
    documento ? `${getCpfCnpjLabel(documento)} ${documento}` : "",
    parceiro?.idJoin ? `ID Join ${parceiro.idJoin}` : "Sem ID Join",
    local,
  ].filter(Boolean);

  const responsavelNome = parceiro?.usuario?.nome || parceiro?.usuarioNome || "";
  const responsavelFoto = parceiro?.usuario?.fotoPerfilUrl || parceiro?.usuarioFotoPerfilUrl || "";
  const nome = nomeExibicaoParceiro(parceiro?.nome) || fallbackNome;

  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:px-6 sm:py-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-muted text-foreground">
            <Building2 className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-display text-xl font-semibold text-foreground sm:text-2xl">
                {onNomeClick ? (
                  <button
                    type="button"
                    onClick={onNomeClick}
                    title="Abrir detalhes do parceiro"
                    className="truncate underline decoration-border underline-offset-4 transition hover:decoration-foreground"
                  >
                    {nome}
                  </button>
                ) : (
                  nome
                )}
              </h1>
              {parceiro?.status ? (
                <StatusBadge status={PARCEIRO_STATUS_LABEL[parceiro.status] || parceiro.status} shape="rounded" />
              ) : null}
            </div>
            <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {detalhes.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-3 lg:border-l lg:border-border lg:pl-6">
          <Avatar className="h-10 w-10 border border-border/70">
            <AvatarImage src={responsavelFoto} alt={responsavelNome || "Sem responsável"} />
            <AvatarFallback className="bg-muted text-foreground">
              {getAvatarInitials(responsavelNome || "Sem responsável")}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Responsável</p>
            <p className="truncate text-sm font-semibold text-foreground">{responsavelNome || "Sem responsável definido"}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
